<template>
  <div class="relative overflow-hidden rounded-lg">
    <!-- Mouse-responsive blob background -->
    <div
      class="blob-container"
      @mouseenter="measureContainer"
      @mousemove="handleMouseMove"
      ref="containerRef"
    >
      <div class="blob-track track-1"><div class="review-blob blob-1"></div></div>
      <div class="blob-track track-2"><div class="review-blob blob-2"></div></div>
      <div class="blob-track track-3"><div class="review-blob blob-3"></div></div>
    </div>

    <!-- Content overlay -->
    <div class="relative z-10  py-20">
      <div class="text-4xl p-8 text-center font-bold">
        Review<span class="text-orange-500">IQ</span>
      </div>
      <div
        class="reactive-text-wrapper sm:w-[50%] mx-auto"
        @pointerenter="handleTextPointerEnter"
        @pointermove="handleTextPointerMove"
        @pointerleave="handleTextPointerLeave"
        ref="textRef"
      >
        <div class="text-tilt" ref="textTiltRef">
          <div class="text-glow"></div>
          <div class="text-content bg-black/60 p-4 rounded">
            <p class="text-2xl">
              ReviewIQ is a powerful and intuitive review management platform we've
              built in-house that helps businesses collect payments and valuable
              feedback from their clients.
              <br />
              <br />
              Perfect the art of review acquisition with ReviewIQ through custom
              review pages, personalized review requests, secure payment processing,
              and insightful metrics.
              <br />
              <br />
              <span class="block text-center">
                Get started for free or request a demo to see how ReviewIQ can help you.
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const containerRef = ref(null);
const textRef = ref(null);
const textTiltRef = ref(null);

let containerRect = null;
let containerRafId = null;
let pendingContainerEvent = null;

let textRect = null;
let textRafId = null;
let pendingTextEvent = null;

// Stored in document coordinates (paired with pageX/pageY below) so the
// cached rect stays valid when the page scrolls.
const measureContainer = () => {
  if (!containerRef.value) return;
  const r = containerRef.value.getBoundingClientRect();
  containerRect = {
    left: r.left + window.scrollX,
    top: r.top + window.scrollY,
    width: r.width,
    height: r.height,
  };
};

const handleMouseMove = (e) => {
  pendingContainerEvent = e;
  if (containerRafId) return;
  containerRafId = requestAnimationFrame(() => {
    containerRafId = null;
    const el = containerRef.value;
    if (!el || !containerRect || !pendingContainerEvent) return;
    el.style.setProperty(
      "--mx",
      ((pendingContainerEvent.pageX - containerRect.left) / containerRect.width).toFixed(4)
    );
    el.style.setProperty(
      "--my",
      ((pendingContainerEvent.pageY - containerRect.top) / containerRect.height).toFixed(4)
    );
  });
};

const TEXT_TILT = 2.5;

const handleTextPointerEnter = (e) => {
  if (e.pointerType !== "mouse" || !textRef.value) return;
  const r = textRef.value.getBoundingClientRect();
  textRect = {
    left: r.left + window.scrollX,
    top: r.top + window.scrollY,
    width: r.width,
    height: r.height,
  };
};

const handleTextPointerMove = (e) => {
  if (e.pointerType !== "mouse") return;
  pendingTextEvent = e;
  if (textRafId) return;

  textRafId = requestAnimationFrame(() => {
    textRafId = null;
    const el = textTiltRef.value;
    if (!el || !textRect || !pendingTextEvent) return;

    const x = (pendingTextEvent.pageX - textRect.left) / textRect.width - 0.5;
    const y = (pendingTextEvent.pageY - textRect.top) / textRect.height - 0.5;

    el.style.setProperty("--rx", `${(-y * TEXT_TILT).toFixed(2)}deg`);
    el.style.setProperty("--ry", `${(x * TEXT_TILT).toFixed(2)}deg`);
    el.style.setProperty("--ty", "-3px");
    el.style.setProperty("--s", "1.01");
  });
};

const handleTextPointerLeave = () => {
  if (textRafId) {
    cancelAnimationFrame(textRafId);
    textRafId = null;
  }
  textRect = null;
  pendingTextEvent = null;

  const el = textTiltRef.value;
  if (!el) return;
  el.style.setProperty("--rx", "0deg");
  el.style.setProperty("--ry", "0deg");
  el.style.setProperty("--ty", "0px");
  el.style.setProperty("--s", "1");
};

let blobObserver = null;

onMounted(() => {
  measureContainer();
  window.addEventListener("resize", measureContainer);
  if ("IntersectionObserver" in window && containerRef.value) {
    blobObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        containerRef.value.classList.toggle("blobs-paused", !entry.isIntersecting);
      });
    });
    blobObserver.observe(containerRef.value);
  }
});

onUnmounted(() => {
  if (containerRafId) cancelAnimationFrame(containerRafId);
  if (textRafId) cancelAnimationFrame(textRafId);
  window.removeEventListener("resize", measureContainer);
  if (blobObserver) {
    blobObserver.disconnect();
    blobObserver = null;
  }
});
</script>

<style scoped>
.blob-container {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%);
  --mx: 0.5;
  --my: 0.5;
  /* Feathered edges using mask */
  mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black 5%,
    black 95%,
    transparent 100%
  ),
  linear-gradient(
    to right,
    transparent 0%,
    black 3%,
    black 97%,
    transparent 100%
  );
  mask-composite: intersect;
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black 5%,
    black 95%,
    transparent 100%
  ),
  linear-gradient(
    to right,
    transparent 0%,
    black 3%,
    black 97%,
    transparent 100%
  );
  -webkit-mask-composite: source-in;
}

/* Track wrappers handle the mouse-follow movement (translate only).
   Their left/top is a static base position set once, never animated. */
.blob-track {
  position: absolute;
  will-change: transform;
  transition: transform 0.4s cubic-bezier(0.25, 0, 0.25, 1);
  pointer-events: none;
}

.track-1 {
  left: -10%;
  top: -5%;
  transform: translate(
    calc((var(--mx) - 0.5) * 300px),
    calc((var(--my) - 0.5) * 350px)
  );
}

.track-2 {
  left: 20%;
  top: -10%;
  transform: translate(
    calc((var(--mx) - 0.5) * 250px),
    calc((var(--my) - 0.5) * 300px)
  );
}

.track-3 {
  left: 40%;
  top: 30%;
  transform: translate(
    calc((var(--mx) - 0.5) * 275px),
    calc((var(--my) - 0.5) * 325px)
  );
}

/* The blobs only run the idle drift keyframes (transform-only). The soft
   edge comes from the gradient's own falloff instead of filter: blur(70px),
   so moving/scaling them never triggers blur re-rasterization. */
.review-blob {
  position: absolute;
  border-radius: 50%;
  opacity: 0.5;
  animation: drift 25s cubic-bezier(0.25, 0, 0.25, 1) infinite;
  will-change: transform;
  pointer-events: none;
}

/* Paused while off-screen (see IntersectionObserver in script). */
.blob-container.blobs-paused .review-blob {
  animation-play-state: paused;
}

.blob-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(
    circle,
    rgba(0, 212, 255, 0.85) 0%,
    rgba(0, 212, 255, 0.35) 45%,
    transparent 70%
  );
  animation-delay: 0s;
}

.blob-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(74, 144, 226, 0.85) 0%,
    rgba(74, 144, 226, 0.35) 45%,
    transparent 70%
  );
  animation-delay: -8s;
}

.blob-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(
    circle,
    rgba(102, 126, 234, 0.85) 0%,
    rgba(102, 126, 234, 0.35) 45%,
    transparent 70%
  );
  animation-delay: -16s;
}

.reactive-text-wrapper {
  position: relative;
  display: block;
  cursor: default;
  perspective: 1000px;
}

.text-tilt {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  /* Depth comes from the parent's `perspective`; no perspective() here. */
  transform: translate3d(0, var(--ty, 0px), 0)
    rotateX(var(--rx, 0deg))
    rotateY(var(--ry, 0deg))
    scale(var(--s, 1));
  transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
}

/* Promote to a GPU layer only while hovered. */
.reactive-text-wrapper:hover .text-tilt {
  will-change: transform;
}

/* Static sheen: fades in purely via composited opacity on hover — no JS,
   no transform, no oversized layer (was inset: -50%, i.e. 4x area). */
.text-glow {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  background: radial-gradient(
    circle at 30% 25%,
    rgba(255, 165, 0, 0.25),
    transparent 65%
  );
  opacity: 0;
  transition: opacity 1.5s ease;
  pointer-events: none;
  z-index: 0;
}

.reactive-text-wrapper:hover .text-glow {
  opacity: 1;
}

.text-content {
  position: relative;
  z-index: 1;
}

@keyframes drift {
  0%, 100% {
    transform: translate(0, 0);
  }
  33% {
    transform: translate(40px, -50px);
  }
  66% {
    transform: translate(-30px, 40px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .review-blob {
    animation: none;
  }
  .blob-track {
    transition: none;
    transform: none;
  }
  .text-tilt {
    transition: none;
    transform: none;
  }
}

/* Touch devices: :hover sticks after a tap, so skip the glow. */
@media (hover: none) {
  .reactive-text-wrapper:hover .text-glow {
    opacity: 0;
  }
}
</style>
